import { Download, Eye, FileText } from 'lucide-react';
import { DOC_TYPE_CONFIG } from '../../constants/booking-details.constants';
import { cn } from '../../lib/utils';
import { BookingDetails } from '../../types/booking-details.types';
import { detectFileType, openPdfInNewTab } from '../../utils/fileConversion';

/**
 * Formate la taille d'un fichier en unités lisibles
 */
function formatFileSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

/**
 * Retourne un label français pour le type MIME
 */
function getMimeLabel(mimeType?: string): string {
  if (!mimeType) return 'Document';
  if (mimeType.includes('pdf')) return 'PDF';
  if (mimeType.includes('jpeg') || mimeType.includes('jpg')) return 'Image JPEG';
  if (mimeType.includes('png')) return 'Image PNG';
  if (mimeType.includes('webp')) return 'Image WebP';
  if (mimeType.includes('image')) return 'Image';
  if (mimeType.includes('word') || mimeType.includes('docx')) return 'Word';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'Excel';
  return 'Fichier';
}

export function DocumentsTab({ booking }: { booking: BookingDetails }) {
  const handleView = (doc: any) => {
    if (!doc.url) return;

    const fileType = detectFileType(doc.fileName || doc.name, doc.mimeType);

    if (fileType === 'pdf') {
      // Pour les PDFs, utiliser le système base64
      openPdfInNewTab({
        url: doc.url,
        type: doc.mimeType || 'application/pdf',
        name: doc.name,
      });
    } else if (fileType === 'image') {
      // Pour les images, ouvrir directement
      window.open(doc.url, '_blank');
    } else {
      // Pour les autres documents, télécharger
      handleDownload(doc.url, doc.fileName || doc.name);
    }
  };

  const handleDownload = (url: string, name: string) => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-6">
      <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-6">
        Documents liés ({booking.documents?.length || 0})
      </h3>
      {booking.documents && booking.documents.length > 0 ? (
        <div className="space-y-3">
          {booking.documents.map((doc, idx) => {
            const config = DOC_TYPE_CONFIG[doc.type] || DOC_TYPE_CONFIG.confirmation;
            const DocIcon = config.icon;
            // Icône déjà gérée via DOC_TYPE_CONFIG
            const sizeStr = formatFileSize((doc as any).fileSize);
            const mimeLabel = getMimeLabel((doc as any).mimeType);
            const hasUrl = doc.url && doc.url.length > 0;

            return (
              <div
                key={idx}
                className={cn(
                  'bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4 transition-colors group',
                  hasUrl ? 'hover:border-primary/30' : 'opacity-90',
                )}
              >
                <div
                  className={cn(
                    'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
                    config.bg,
                    config.text,
                  )}
                >
                  <DocIcon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">{doc.name}</p>
                  {(doc as any).fileName && (doc as any).fileName !== doc.name && (
                    <p className="text-[11px] text-slate-400 truncate mt-0.5" title={(doc as any).fileName}>
                      {(doc as any).fileName}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-slate-500">{doc.date}</span>
                    {mimeLabel && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="text-xs text-slate-500">{mimeLabel}</span>
                      </>
                    )}
                    {sizeStr && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="text-xs text-slate-500">{sizeStr}</span>
                      </>
                    )}
                  </div>
                </div>
                {hasUrl ? (
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      className="p-2 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                      title="Visualiser"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleView(doc);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      className="p-2 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                      title="Télécharger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(doc.url!, doc.name);
                      }}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <span className="text-xs text-slate-400 italic">Non disponible</span>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 font-medium">Aucun document associé</p>
          <p className="text-xs text-slate-400 mt-1">Les documents joints à cette réservation apparaîtront ici</p>
        </div>
      )}
    </div>
  );
}
